import { Link } from 'react-router-dom';
import { GraduationCap, Coins, Briefcase, ImageIcon, Clock, Smartphone, Palette } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthAwareLink from '../components/AuthAwareLink';
import Seo from '../seo/Seo';
import { buildFaqSchema } from '../seo/structuredData';

const faqs = [
  {
    Icon: GraduationCap,
    q: 'Do I need design experience?',
    a: 'No. The workflow is beginner-friendly and guides you step by step from upload to result. No design background is required.',
  },
  {
    Icon: Coins,
    q: 'How are credits used?',
    a: 'Each AI redesign consumes credits depending on your selected quality and mode. Credits are purchased as one-time packs and never expire.',
  },
  {
    Icon: Briefcase,
    q: 'Can I use my generated designs commercially?',
    a: 'Yes, commercial usage can be enabled on paid plans based on your terms.',
  },
  {
    Icon: Palette,
    q: 'How many design styles are available?',
    a: 'MyRoomsAI supports a wide range of styles including Modern, Minimalist, Scandinavian, Industrial, Bohemian, Coastal, Luxury, and more. New styles are added regularly.',
  },
  {
    Icon: ImageIcon,
    q: 'What image formats are supported for upload?',
    a: 'You can upload JPEG, PNG, and WEBP photos. For best results, use a clear, well-lit photo taken from a corner or doorway to show the full room.',
  },
  {
    Icon: Clock,
    q: 'How long does AI generation take?',
    a: 'Most redesigns are generated in under 30 seconds. The exact time depends on server load and the complexity of the selected style.',
  },
  {
    Icon: Smartphone,
    q: 'Can I use MyRoomsAI on mobile?',
    a: 'Yes. MyRoomsAI is fully responsive and works on mobile browsers. You can upload photos directly from your phone camera roll.',
  },
];

function FaqPage() {
  const newDesignTo = '/ai-tools/ai-room-decoration';

  return (
    <div className="page landing-bg">
      <Seo
        title="Interior AI FAQ | AI Room Designer FAQ"
        description="Frequently asked questions about MyRoomsAI — learn how credits work, what design styles are available, and how to get the best results from AI room redesign."
        canonicalPath="/faq/"
        keywords="MyRoomsAI FAQ, AI interior design questions, credits explained, room design styles, AI redesign help"
        structuredData={[buildFaqSchema(faqs)]}
      />
      <Navbar />

      <section className="container section fp-hero">
        <span className="credits-badge credits-badge--mint">Got questions?</span>
        <h1 className="fp-hero-heading">Frequently Asked Questions</h1>
        <p className="credits-subtitle">
          Everything you need to know about MyRoomsAI.
        </p>
      </section>

      <section className="container section fp-grid-section">
        <div className="cards faq-grid">
          {faqs.map(({ Icon, q, a }) => (
            <article key={q} className="card faq-card">
              <span className="faq-card-icon-badge">
                <Icon size={20} strokeWidth={1.8} className="fp-lucide-icon" aria-hidden="true" />
              </span>
              <h3>{q}</h3>
              <p>{a}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="container section fp-related-section">
        <h2>More to Explore</h2>
        <p>See what MyRoomsAI can do for different room types, or browse real results from other users.</p>
        <div className="fp-related-links">
          <Link to="/real-transformations" className="btn btn-ghost">Real Transformations</Link>
          <Link to="/room-types/living-room" className="btn btn-ghost">Living Room Ideas</Link>
          <Link to="/room-types/bedroom" className="btn btn-ghost">Bedroom Ideas</Link>
          <Link to="/ai-tools/ai-room-decoration" className="btn btn-ghost">How It Works</Link>
        </div>
      </section>

      <section className="container section fp-cta-section">
        <div className="fp-cta-card fp-cta-card--trust">
          <div className="fp-cta-inner">
            <div className="fp-cta-copy">
              <h2 className="fp-cta-heading">Ready to get started?</h2>
              <p className="fp-cta-text">
                Upload your room photo and get a beautiful AI redesign in minutes.
              </p>
            </div>
            <div className="fp-cta-actions-wrap">
              <div className="fp-cta-actions">
                <Link to={newDesignTo} className="btn btn-primary">
                  Start New Design
                </Link>
                <AuthAwareLink spaPath="/features" staticPath="/features/" className="btn btn-ghost">
                  See Features
                </AuthAwareLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default FaqPage;
